import { useSelector, shallowEqual } from 'react-redux';
import NsContent from 'components/common/ns-content/NsContent';
import NsColumn from '@britishgasenergy/react-nucleus/dist/nuc-components/NsColumn';

import { NsBackCta } from 'components/common/ns-back-cta/NsBackCta';
import { NsSecondaryCta } from 'components/common/ns-secondary-cta/NsSecondaryCta';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsTitle } from 'components/common/ns-title/NsTitle';
import { NsAlert } from 'components/common/ns-alert/NsAlert';
import { NsAnchor } from 'components/common/ns-anchor/NsAnchor';
import { useCurrentProduct } from 'components/direct-sales/quote/hooks/useCurrentProduct';
import { ChoooseTariffCard } from 'components/direct-sales/tariff-details/components/ChooseTariffCard';
import { useResponsive } from 'hooks/useResponsive';
import { useIsLoginEnabledJourney } from 'hooks/useIsLoginEnabledJourney';
import { CostBreakDown } from 'components/direct-sales/tariff-details/components/CostBreakDown';
import { useNavigation } from 'hooks/useNavigation';
import { FUEL_TYPES } from 'config/app-config';
import { TariffFeatures } from 'components/direct-sales/quote/components/TariffCards/TariffFeatures';
import { AboutTariff } from 'components/direct-sales/tariff-details/components/AboutTariff';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { NsAccordion } from 'components/common/ns-accordion/NsAccordion';
import { NsPrice } from 'components/common/ns-price/NsPrice';
import MoreInformation from 'components/direct-sales/quote/components/MoreInformation';

export const testId = 'tariffDetailsTestId';
export const testIdAgenttariffAlert = 'testIdAgenttariffAlert';
export const testIdSmartMeterText = 'testIdSmartMeterText';

export const TariffDetails = () => {
  const product = useCurrentProduct();
  const fuelType = useSelector(selectFuelType, shallowEqual);
  const { isDesktop } = useResponsive();
  const { next, eventTypes } = useNavigation();

  const {
    productName,
    gasQuote,
    electricityQuote,
    isTOU,
    monthlyAmount
  } = product;

  const isAgentJourney = useIsLoginEnabledJourney();

  let alertMessageAsPerFuelSelection;

  switch (true) {
    case fuelType.gas && fuelType.electric:
      alertMessageAsPerFuelSelection = (
        <>
          Your yearly personal projection for electricity is{' '}
          <b>
            <NsPrice amount={electricityQuote?.annualAmount} />
          </b>{' '}
          and for gas it is{' '}
          <b>
            <NsPrice amount={gasQuote?.annualAmount} />
          </b>{' '}
          at a monthly payment of{' '}
          <b>
            <NsPrice amount={monthlyAmount} />
          </b>
          .
        </>
      );
      break;
    case fuelType.gas:
      alertMessageAsPerFuelSelection = (
        <>
          Your yearly personal projection for gas is{' '}
          <b>
            <NsPrice amount={gasQuote?.annualAmount} />
          </b>{' '}
          at a monthly payment of{' '}
          <b>
            <NsPrice amount={monthlyAmount} />
          </b>
          .
        </>
      );
      break;
    default:
      alertMessageAsPerFuelSelection = (
        <>
          Your yearly personal projection for electricity is{' '}
          <b>
            <NsPrice amount={electricityQuote?.annualAmount} />
          </b>{' '}
          at a monthly payment of{' '}
          <b>
            <NsPrice amount={monthlyAmount} />
          </b>
          .
        </>
      );
  }

  const handleGoNext = () => {
    next({ eventType: eventTypes.NEXT });
  };

  return (
    <NsPanel testId={testId}>
      <NsBackCta />
      <NsContent>
        <NsTitle title={productName || ''} />
        {isAgentJourney && (
          <NsAlert
            heading="Ask customers before choosing the tariff"
            state="info"
            testId={testIdAgenttariffAlert}
          >
            <p>
              So just to confirm, you’ve chosen to see our{' '}
              <b>{productName} tariff.</b> {alertMessageAsPerFuelSelection}
              {isTOU && (
                <>
                  {' '}
                  To be eligible for this tariff, your smart meter(s) will
                  automatically provide half-hourly meter readings.
                </>
              )}
            </p>
            <p>
              Would you like to hear the tariff information, unit rates and
              standing charges before deciding to choose this tariff or will you
              be happy to receive them in writing?
            </p>
            <p className="h6">
              <strong>
                If customer chooses not to hear the information, please read the
                following verbatim.
              </strong>
            </p>
            <p>
              “Please read your contract pack carefully once you have received
              it.”
            </p>
          </NsAlert>
        )}

        <ChoooseTariffCard
          shouldShow={!isDesktop}
          product={product}
          handleGoNext={handleGoNext}
          secondaryCta={() => (
            <NsSecondaryCta href="#costBreakdown" icon="chevron-down">
              See cost breakdown
            </NsSecondaryCta>
          )}
        />

        <NsColumn hockeypitch="2:1">
          <div id="summary">
            <AboutTariff product={product} />
            <h4>Tariff details</h4>
            <TariffFeatures product={product} isFixed={product.isFixed} />
            <p>
              See Terms & Conditions under{' '}
              <NsAnchor href="#moreInformation">More information</NsAnchor> for
              further details
            </p>
          </div>
          <ChoooseTariffCard
            shouldShow={isDesktop}
            product={product}
            handleGoNext={handleGoNext}
            secondaryCta={() => (
              <NsSecondaryCta href="#costBreakdown" icon="chevron-down">
                See cost breakdown
              </NsSecondaryCta>
            )}
          />
        </NsColumn>
      </NsContent>

      <NsColumn id="costBreakdown" hockeypitch="2">
        <CostBreakDown til={electricityQuote} fuel={FUEL_TYPES.ELECTRIC} />
        <CostBreakDown til={gasQuote} fuel={FUEL_TYPES.GAS} />
      </NsColumn>

      <ChoooseTariffCard
        shouldShow={!isDesktop}
        product={product}
        handleGoNext={handleGoNext}
        secondaryCta={() => (
          <NsSecondaryCta href="#summary" icon="chevron-up">
            See summary
          </NsSecondaryCta>
        )}
      />
      <NsAccordion>
        <MoreInformation />
      </NsAccordion>
    </NsPanel>
  );
};

export default TariffDetails;
